import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  AdminRoofSubstructure,
  UpdateRoofSubstructureNewImageBody,
  UpdateRoofSubstructureRemoveImageBody,
} from "../../../../../http/patrol/types/roofSubstructures";
import { useRoofSubstructuresApi } from "../../../../../http/patrol/useRoofSubstructures";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import { default as useImageUploader } from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import TranslatableField from "../../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface UpdateRoofSubstructureForm {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  seamHeightRequired: boolean;
}

interface RoofSubstructureDetailsProps {
  roofSubstructure?: AdminRoofSubstructure;
  disabled?: boolean;
}

const RoofSubstructureForm: FC<RoofSubstructureDetailsProps> = ({
  roofSubstructure,
  disabled,
}: RoofSubstructureDetailsProps) => {
  const i18n = useI18n();
  const api = useApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const roofSubstructuresApi = useRoofSubstructuresApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: roofSubstructure?.image,
    isLoading: !roofSubstructure || (!!roofSubstructure && !roofSubstructure?.id),
    disabled,
  });

  const defaultValues: UpdateRoofSubstructureForm = {
    name: roofSubstructure?.name ?? null,
    nameIt: roofSubstructure?.nameIt ?? null,
    nameDe: roofSubstructure?.nameDe ?? null,
    nameEs: roofSubstructure?.nameEs ?? null,
    nameFr: roofSubstructure?.nameFr ?? null,
    order: roofSubstructure?.order ?? 50,
    visibility: roofSubstructure?.visibility ?? "hidden",
    seamHeightRequired: roofSubstructure?.seamHeightRequired ?? false,
  };

  const submit = async (body: UpdateRoofSubstructureNewImageBody | UpdateRoofSubstructureRemoveImageBody) => {
    try {
      await roofSubstructuresApi.updateAdminRoofSubstructure(roofSubstructure?.id!, body);
      onSave();
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = () => async (formData: UpdateRoofSubstructureForm) => {
    setIsSubmitting(true);

    const commonBody = {
      name: formData.name?.trim()!,
      nameIt: formData.nameIt?.trim() ?? null,
      nameDe: formData.nameDe?.trim() ?? null,
      nameEs: formData.nameEs?.trim() ?? null,
      nameFr: formData.nameFr?.trim() ?? null,
      order: formData.order!,
      visibility: formData.visibility,
      seamHeightRequired: formData.seamHeightRequired,
    };

    if (newImage) {
      try {
        const signedUrl = await roofSubstructuresApi.generateUploadImageUrl(
          roofSubstructure?.id!,
          newImage.name,
          newImage.type,
        );
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsSubmitting(false);
          return;
        }
        const body: UpdateRoofSubstructureNewImageBody = {
          ...commonBody,
          image: newImage.name,
        };
        await submit(body);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const body: UpdateRoofSubstructureRemoveImageBody = {
        ...commonBody,
        removeImage: removeImage,
      };
      await submit(body);
    }
  };

  return (
    <Form<UpdateRoofSubstructureForm>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [roofSubstructure]);

        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
        }, [register]);

        const values = watch();

        const seamHeightRequired = !!values.seamHeightRequired;

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => {
                return (
                  <TranslatableField
                    field="name"
                    formValues={values}
                    setFormValue={setValue}
                    dialogTitle={i18n.translation.common.name}
                  >
                    <Input
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled}
                      {...register("name", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                );
              }}
            </FormField>
            <FormField label={i18n.translation.common.seamHeightRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={seamHeightRequired}
                  disabled={disabled}
                  onChange={(checked) => setValue("seamHeightRequired", checked)}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={disabled}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default RoofSubstructureForm;
